import { CategoryListInterface } from "../interfaces/CategoryListInterface";
import { ProductInterface } from "../interfaces/ProductInterface";
import { FilterListInterface } from "../interfaces/FilterListInterface";
import { ProductTypeEnum } from "../../pages/productRangePage/ProductRangeSlice";

const hasActiveFilters = (selectedFilters: {
    [filterId: string]: {
        id: number;
        options: { id: string; value: string | number }[];
    };
}): boolean => {
    return (
        Object.keys(selectedFilters).length > 0 &&
        Object.values(selectedFilters).some(
            (filter) => filter.options.length > 0,
        )
    );
};

const productMatchesFilter = (
    product: ProductInterface,
    filter: FilterListInterface,
    selectedOptions: (string | number)[],
): boolean => {
    if (!selectedOptions.length || !product.filterAttributes) return false;

    const productAttribute = product.filterAttributes.find(
        (attr) => attr.code === filter.code,
    );
    if (!productAttribute) return false;

    return selectedOptions.some((selectedValue) => {
        if (Array.isArray(productAttribute.value)) {
            return productAttribute.value.some((val) => val === selectedValue);
        }

        if (
            typeof productAttribute.value === "object" &&
            productAttribute.value !== null
        ) {
            return Object.prototype.hasOwnProperty.call(
                productAttribute.value,
                selectedValue,
            );
        }

        if (typeof productAttribute.value === "string") {
            // Resolved attribute values for multi-option attributes are
            // comma-joined display labels (e.g. "Ground coffee, Whole beans").
            return productAttribute.value
                .split(",")
                .map((value) => value.trim())
                .includes(String(selectedValue));
        }

        return productAttribute.value === selectedValue;
    });
};

export const applyFilters = (
    products: ProductInterface[],
    filters: FilterListInterface[],
    selectedFilters: {
        [filterId: string]: {
            id: number;
            options: { id: string; value: string | number }[];
        };
    },
): ProductInterface[] => {
    if (filters.length === 0 || !hasActiveFilters(selectedFilters)) {
        return products;
    }

    return products.filter((product: ProductInterface) => {
        return Object.entries(selectedFilters).every(
            ([filterId, filterData]) => {
                if (!filterData.options.length) return true;

                const filter = filters.find(
                    (f) => f.id.toString() === filterId,
                );
                if (!filter) return false;

                const selectedValues = filterData.options.map(
                    (option) => option.value,
                );
                return productMatchesFilter(product, filter, selectedValues);
            },
        );
    });
};

export const filterCategoriesByType = (
    categories: CategoryListInterface[],
    productType: ProductTypeEnum,
): CategoryListInterface[] => {
    if (productType === ProductTypeEnum.ACCESSORIES) {
        return categories.filter(
            (category) => category.code === ProductTypeEnum.ACCESSORIES,
        );
    }
    return categories.filter(
        (category) => category.code !== ProductTypeEnum.ACCESSORIES,
    );
};
